import { router } from '@inertiajs/react';
import { type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errorMessages } from './errors';

type Step = 'details' | 'verify';

interface Props {
  githubUsername: string;
  githubAvatarUrl: string | null;
  /** Everything above the card: the pre-title, the greeting and the avatar. */
  header: ReactNode;
  /** Where the verified name/age/code go, and where a fresh code is asked for. */
  submitUrl: string;
  codeUrl: string;
  /**
   * The invitee flow only: shown instead of the code card when the server refuses
   * because this hacker already belongs to another team.
   */
  conflictCopy?: (role: 'leader' | 'member') => string;
  conflictAction?: (role: 'leader' | 'member') => string;
}

// The identity step of both entrances (identity-form.tsx and invitee-identity-form.tsx,
// which were the same form with one extra card).
export function IdentityForm({
  githubUsername,
  githubAvatarUrl,
  header,
  submitUrl,
  codeUrl,
  conflictCopy,
  conflictAction,
}: Props) {
  const [step, setStep] = useState<Step>('details');
  const [isPending, setIsPending] = useState(false);

  const [fullName, setFullName] = useState('');
  const [age, setAge] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [conflict, setConflict] = useState<'leader' | 'member' | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const requestCode = (onSent: () => void) => {
    setIsPending(true);
    router.post(
      codeUrl,
      { email },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: onSent,
        onError: (errors) =>
          toast.error(
            errorMessages(errors).base ?? 'No se pudo enviar el código',
          ),
        onFinish: () => setIsPending(false),
      },
    );
  };

  const sendCode = () => {
    if (!fullName.trim() || fullName.trim().length < 2) {
      toast.error('Ingresa tu nombre completo');
      return;
    }
    const ageNum = Number(age);
    if (!Number.isFinite(ageNum) || ageNum < 14 || ageNum > 99) {
      toast.error('Ingresa una edad válida');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Ingresa un correo válido');
      return;
    }

    requestCode(() => {
      toast.success(`Código enviado a ${email}`);
      setStep('verify');
      setResendCooldown(30);
    });
  };

  const resendCode = () => {
    if (resendCooldown > 0) return;
    requestCode(() => {
      toast.success(`Código reenviado a ${email}`);
      setCode('');
      setResendCooldown(30);
    });
  };

  const verify = (leaveCurrentTeam: boolean) => {
    setIsPending(true);
    router.post(
      submitUrl,
      { fullName: fullName.trim(), age: Number(age), email, code, leaveCurrentTeam },
      {
        // The name, the age and the code live only in this component's state; a
        // refused verification must not remount the page and lose them.
        preserveState: true,
        onError: (errors) => {
          const messages = errorMessages(errors);
          if (messages.reason === 'already_applying' && conflictCopy) {
            setConflict(messages.currentRole === 'leader' ? 'leader' : 'member');
            return;
          }
          toast.error(messages.base ?? 'Código inválido');
        },
        onFinish: () => setIsPending(false),
      },
    );
  };

  const verifyAndContinue = () => {
    if (!/^\d{6}$/.test(code)) {
      toast.error('El código tiene 6 dígitos');
      return;
    }
    verify(false);
  };

  return (
    <div className="mx-auto max-w-xl space-y-8 px-4 py-12">
      <header className="space-y-2">
        {header}
        {githubAvatarUrl && (
          <img
            src={githubAvatarUrl}
            alt={githubUsername}
            className="h-12 w-12 rounded-full border"
          />
        )}
      </header>

      {step === 'details' && (
        <div className="space-y-4 border-2 border-primary bg-background/80 p-6">
          <div className="space-y-1">
            <Label htmlFor="fullName">Nombre completo</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={isPending}
              autoComplete="name"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="age">Edad</Label>
            <Input
              id="age"
              type="number"
              min={14}
              max={99}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              disabled={isPending}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="email">Correo de contacto</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isPending}
              autoComplete="email"
            />
            <p className="text-muted-foreground text-xs">
              Te enviamos un código de verificación a este correo.
            </p>
          </div>
          <Button onClick={sendCode} disabled={isPending} className="w-full">
            {isPending ? 'Enviando...' : 'Enviar código'}
          </Button>
        </div>
      )}

      {step === 'verify' && conflict && conflictCopy && conflictAction && (
        <div className="space-y-4 border-2 border-destructive bg-background/80 p-6">
          <h2 className="font-bold text-lg">Ya estás en otro equipo</h2>
          <p className="text-muted-foreground text-sm">
            {conflictCopy(conflict)}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setConflict(null)}
              disabled={isPending}
              className="flex-1"
            >
              Volver
            </Button>
            <Button
              onClick={() => verify(true)}
              disabled={isPending}
              className="flex-1"
            >
              {isPending ? 'Procesando...' : conflictAction(conflict)}
            </Button>
          </div>
        </div>
      )}

      {step === 'verify' && !conflict && (
        <div className="space-y-4 border-2 border-primary bg-background/80 p-6">
          <p className="text-sm">
            Te enviamos un código de 6 dígitos a <strong>{email}</strong>.
          </p>
          <div className="space-y-1">
            <Label htmlFor="code">Código</Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={isPending}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              ¿No te llegó el código?
            </span>
            <button
              type="button"
              onClick={resendCode}
              disabled={isPending || resendCooldown > 0}
              className="font-medium text-primary underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
            >
              {resendCooldown > 0
                ? `Reenviar en ${resendCooldown}s`
                : 'Reenviar código'}
            </button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setStep('details')}
              disabled={isPending}
              className="flex-1"
            >
              Volver
            </Button>
            <Button
              onClick={verifyAndContinue}
              disabled={isPending}
              className="flex-1"
            >
              {isPending ? 'Verificando...' : 'Verificar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
